import { show } from '@intercom/messenger-js-sdk';
import { createFileRoute, Navigate } from '@tanstack/react-router';
import { BookOpen, ExternalLink, Mail, MessageCircle } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { Button } from '@apps/web/components/ui/button';
import {
    SettingsPageShell,
    useSettingsGates,
} from '@apps/web/features/settings';
import { isExtensionContext } from '@apps/web/lib/extension_context';
import { getRuntimeConfig } from '@apps/web/lib/runtime_config';

export const Route = createFileRoute('/_app/settings/help-center')({
    component: HelpCenterPage,
});

const DOCUMENTATION_URL = 'https://docs.phoebe.work/docs';

// How long the darken + ring + wiggle intro runs before the messenger opens.
const SPOTLIGHT_DURATION_MS = 2200;
// The Intercom launcher sits at z-index ~2147483000; keeping the overlay just
// below lets the bubble shine through the darkened screen untouched.
const SPOTLIGHT_OVERLAY_Z_INDEX = 2147482990;
// Fallback launcher center when the element isn't found: 20px edge padding
// plus half of the 48px bubble, with the provider's 40px vertical padding.
const LAUNCHER_FALLBACK_LEFT_PX = 44;
const LAUNCHER_FALLBACK_BOTTOM_PX = 64;

function findIntercomLauncher(): HTMLElement | null {
    return document.querySelector<HTMLElement>(
        '.intercom-lightweight-app-launcher, .intercom-launcher',
    );
}

// A miniature chat bubble with pulsing "typing" dots — a lightweight,
// asset-free illustration of the Intercom messenger.
function ChatBubbleAnimation() {
    return (
        <div className="bg-primary/10 flex h-11 w-11 items-center justify-center rounded-lg">
            <div className="bg-primary flex h-7 w-9 items-center justify-center gap-1 rounded-lg rounded-bl-none">
                <span className="bg-primary-foreground/90 h-1 w-1 animate-bounce rounded-full" />
                <span className="bg-primary-foreground/90 h-1 w-1 animate-bounce rounded-full [animation-delay:150ms]" />
                <span className="bg-primary-foreground/90 h-1 w-1 animate-bounce rounded-full [animation-delay:300ms]" />
            </div>
        </div>
    );
}

// Darkens the screen and draws a pulsing ring around the Intercom launcher,
// which stays lit above the overlay thanks to its own (higher) z-index.
function IntercomSpotlight({
    center,
    onDismiss,
}: {
    readonly center: { readonly x: number; readonly y: number };
    readonly onDismiss: () => void;
}) {
    return (
        <div
            className="fixed inset-0"
            style={{ zIndex: SPOTLIGHT_OVERLAY_Z_INDEX }}
            onClick={onDismiss}
            aria-hidden="true"
        >
            <style>{`
                @keyframes help-center-intercom-wiggle {
                    0%, 100% { transform: rotate(0deg) scale(1); }
                    20% { transform: rotate(-8deg) scale(1.08); }
                    40% { transform: rotate(8deg) scale(1.08); }
                    60% { transform: rotate(-6deg) scale(1.04); }
                    80% { transform: rotate(6deg) scale(1.04); }
                }
                .help-center-intercom-wiggle {
                    animation: help-center-intercom-wiggle 0.9s ease-in-out 2;
                    transform-origin: center;
                }
            `}</style>
            <div className="animate-in fade-in-0 absolute inset-0 bg-black/50" />
            <div
                className="absolute h-20 w-20 animate-ping rounded-full border-2 border-white/80"
                style={{ left: center.x - 40, top: center.y - 40 }}
            />
            <div
                className="absolute h-20 w-20 rounded-full border-2 border-white/90"
                style={{ left: center.x - 40, top: center.y - 40 }}
            />
        </div>
    );
}

function HelpCenterPage() {
    const { gates } = useSettingsGates();
    // The messenger is booted globally by IntercomProvider whenever an app id
    // is configured (it is null in local dev and inside the extension).
    const intercomAvailable =
        getRuntimeConfig().intercomAppId !== null && !isExtensionContext();
    const [spotlightCenter, setSpotlightCenter] = useState<{
        readonly x: number;
        readonly y: number;
    } | null>(null);
    const spotlightTimeoutRef = useRef<number | null>(null);

    const endSpotlight = useCallback((openMessenger: boolean) => {
        if (spotlightTimeoutRef.current !== null) {
            window.clearTimeout(spotlightTimeoutRef.current);
            spotlightTimeoutRef.current = null;
        }
        findIntercomLauncher()?.classList.remove('help-center-intercom-wiggle');
        setSpotlightCenter(null);
        if (openMessenger) {
            show();
        }
    }, []);

    useEffect(() => {
        // Leaving the page mid-animation must not leave a stray timer or a
        // wiggle class behind on Intercom's launcher.
        return () => {
            if (spotlightTimeoutRef.current !== null) {
                window.clearTimeout(spotlightTimeoutRef.current);
            }
            findIntercomLauncher()?.classList.remove(
                'help-center-intercom-wiggle',
            );
        };
    }, []);

    const handleOpenLiveChat = useCallback(() => {
        const launcher = findIntercomLauncher();
        const rect = launcher?.getBoundingClientRect();
        launcher?.classList.add('help-center-intercom-wiggle');
        setSpotlightCenter({
            x: rect ? rect.left + rect.width / 2 : LAUNCHER_FALLBACK_LEFT_PX,
            y: rect
                ? rect.top + rect.height / 2
                : window.innerHeight - LAUNCHER_FALLBACK_BOTTOM_PX,
        });
        spotlightTimeoutRef.current = window.setTimeout(() => {
            endSpotlight(true);
        }, SPOTLIGHT_DURATION_MS);
    }, [endSpotlight]);

    if (!gates.canManageOrgSettings) {
        return <Navigate to="/settings" replace={true} />;
    }

    return (
        <SettingsPageShell
            title="Help Center"
            description="Find Phoebe support and documentation."
        >
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                <section className="bg-card flex flex-col rounded-lg border p-5 shadow-sm">
                    <div className="bg-primary/10 flex h-11 w-11 items-center justify-center rounded-lg">
                        <BookOpen className="text-primary h-5 w-5" />
                    </div>
                    <h2 className="text-foreground mt-4 text-base font-semibold">
                        Documentation
                    </h2>
                    <p className="text-muted-foreground mt-1 text-sm">
                        Step-by-step guides for every Phoebe feature —
                        scheduling, call handling, templates, and more.
                    </p>
                    <div className="mt-4 flex-1" />
                    <Button asChild={true} className="w-fit">
                        <a
                            href={DOCUMENTATION_URL}
                            target="_blank"
                            rel="noreferrer"
                        >
                            Browse the docs
                            <ExternalLink className="ml-2 h-4 w-4" />
                        </a>
                    </Button>
                </section>
                <section className="bg-card flex flex-col rounded-lg border p-5 shadow-sm">
                    <ChatBubbleAnimation />
                    <h2 className="text-foreground mt-4 text-base font-semibold">
                        Live chat
                    </h2>
                    <p className="text-muted-foreground mt-1 text-sm">
                        The fastest way to reach us. Click the chat bubble in
                        the bottom-left corner of any page
                        {intercomAvailable ? ' — or the button below — ' : ' '}
                        to message the Phoebe team, and we’ll reply right in the
                        app.
                    </p>
                    <div className="mt-4 flex-1" />
                    {intercomAvailable ? (
                        <Button
                            className="w-fit"
                            onClick={handleOpenLiveChat}
                            disabled={spotlightCenter !== null}
                        >
                            <MessageCircle className="mr-2 h-4 w-4" />
                            Open live chat
                        </Button>
                    ) : null}
                </section>
            </div>
            <p className="text-muted-foreground mt-6 text-sm">
                Prefer email? Reach us any time at{' '}
                <a
                    href="mailto:success@phoebe.work"
                    className="text-foreground inline-flex items-center gap-1 font-medium underline underline-offset-2"
                >
                    <Mail className="h-3.5 w-3.5" />
                    success@phoebe.work
                </a>
                .
            </p>
            {spotlightCenter !== null ? (
                <IntercomSpotlight
                    center={spotlightCenter}
                    onDismiss={() => endSpotlight(true)}
                />
            ) : null}
        </SettingsPageShell>
    );
}
